import type { CheckoutBranding as GQLItem } from '../generated/checkout.graphql';
import * as CheckoutAttribution from './CheckoutAttribution';
import * as CheckoutFavicon from './CheckoutFavicon';

/**
 * The per-host white-label branding the checkout renders under: the brand name shown in the cart
 * footer and the page title, the icons, and the attribution line the backend resolved for the
 * host. It is the customer-facing slice of the same `whitelabel_configs` record the superadmin
 * screens edit through `Internal/Whitelabel/WhitelabelConfig.ts`.
 *
 * The wire type also carries `known`, which stays a transport concern — whether the host has a
 * config at all. `Api.getBranding` consumes it rather than passing it on, so an unconfigured host
 * reaches callers as `undefined` instead of an object they would have to inspect.
 */
export type CheckoutBranding = {
  brandName?: string;
  landingUrl?: string;
  attribution?: CheckoutAttribution.CheckoutAttribution;
  favicons: CheckoutFavicon.CheckoutFavicon[];
};

// An optional property keeps callers passing a generated type assignable with no cast.
export type CheckoutBrandingPayload = GQLItem & {
  attribution?: CheckoutAttribution.CheckoutAttributionPayload | null;
};

/**
 * Mapped field by field rather than through `take`: both fields are nullable on the wire, and a
 * `null` reaching a `T | undefined` field renders as the literal string "null" in an href.
 */
export const fromGql = (item: CheckoutBrandingPayload): CheckoutBranding => ({
  brandName: item.brandName ?? undefined,
  landingUrl: item.landingUrl ?? undefined,
  attribution: CheckoutAttribution.fromGql(item.attribution),
  favicons: CheckoutFavicon.fromGqlCollection(item.favicons),
});
